import { isPlainObj } from '@rehaje/shared'

import { UnknownSchema, VoidSchema } from '../../constants'
import { SchemaDefinition } from '../../models/SchemaDefinition'

import type { SchemaVoidSpecific, TypedSchema } from '../../types'

export class SchemaVoidDefinition extends SchemaDefinition {
  override defaultsMerge(value: any) {
    return value
  }

  override getIn(schema: SchemaVoidSpecific, path: PropertyKey[]) {
    if (path.length === 0) return schema
    return UnknownSchema
  }

  override infer(
    target?: any,
    schema?: SchemaVoidSpecific,
    path: PropertyKey[] = []
  ) {
    const types: TypedSchema[] = []
    for (const type of schema?.types ?? []) {
      const inferred = super.infer(target, type, path)
      if (inferred.type !== 'Schema.Void') types.push(type)
      else if (inferred.types?.length) {
        for (const type of inferred.types) {
          if (type.type !== 'Schema.Void') types.push(type)
        }
      }
    }
    return types.length === 0
      ? VoidSchema
      : ({ ...schema, type: 'Schema.Void', types } as TypedSchema)
  }

  override inferOnlySchema(
    schema: SchemaVoidSpecific,
    path: PropertyKey[] = []
  ) {
    const types: TypedSchema[] = []
    for (const type of types) {
      const inferred = super.inferOnlySchema(type, path)
      if (inferred.type !== 'Schema.Void') types.push(type)
      else if (inferred.types?.length) {
        for (const type of inferred.types) {
          if (type.type !== 'Schema.Void') types.push(type)
        }
      }
    }
    if (types.length === 0) return VoidSchema
    else if (types.length === 1) return types[0]
    else return { ...schema, type: 'Schema.Void', types } as TypedSchema
  }

  override inferTypeScript(): string {
    return `void`
  }

  override prune(target: any): any {
    return target
  }

  override transform(
    target: any,
    schema: SchemaVoidSpecific,
    path: PropertyKey[],
    parent: any
  ) {
    if (isPlainObj(parent)) {
      const inferred = this.infer(target, schema, path)
      if (inferred.type === 'Schema.Void' && inferred.types?.length) {
        for (const type of inferred.types) {
          const name = type.name
          if (name !== undefined && parent[name] !== undefined) {
            parent[name] = super.transform(
              parent[name],
              type,
              path.concat(name),
              parent
            )
          }
        }
      }
    }
    return target
  }

  override validate(
    target: any,
    schema: SchemaVoidSpecific,
    path: PropertyKey[],
    parent: any,
    recursion: boolean
  ) {
    if (isPlainObj(parent) && recursion) {
      const inferred = this.infer(target, schema, path)
      if (inferred.type === 'Schema.Void' && inferred.types?.length) {
        for (const type of inferred.types) {
          const name = type.name
          if (name !== undefined && parent[name] !== undefined) {
            super.validate(parent[name], type, path.concat(name), parent)
          }
        }
      }
    }
  }
}
